import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const UNIT_OPTIONS = ['Kg', 'Liter', 'Gram', 'ML', 'Piece', 'Meter', 'Bag', 'Drum'];

const blankItem = () => ({
  product_id: '',
  batch_no: '',
  quantity: '',
  unit: 'Kg',
  packaging_qty: '',
  remarks: '',
});

const GatePassFormModal = ({ gatePass, onClose, onSaved }) => {
  const isEdit = Boolean(gatePass);

  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);

  const [form, setForm] = useState({
    direction: 'outward', // 'outward' (to customer) or 'inward' (from vendor)
    gp_date: new Date().toISOString().split('T')[0],
    party_id: '',
    rider_name: '',
    rider_cnic: '',
    rider_phone: '',
    vehicle_no: '',
    status: 'issued',
    remarks: '',
    items: [blankItem()],
  });

  const [submitting, setSubmitting] = useState(false);

  // Hydrate when editing
  useEffect(() => {
    if (gatePass) {
      setForm({
        direction: gatePass.direction || 'outward',
        gp_date: gatePass.gp_date ? gatePass.gp_date.split('T')[0] : new Date().toISOString().split('T')[0],
        party_id: gatePass.party_id || '',
        rider_name: gatePass.rider_name || '',
        rider_cnic: gatePass.rider_cnic || '',
        rider_phone: gatePass.rider_phone || '',
        vehicle_no: gatePass.vehicle_no || '',
        status: gatePass.status || 'issued',
        remarks: gatePass.remarks || '',
        items: (gatePass.items && gatePass.items.length > 0)
          ? gatePass.items.map((it) => ({
              product_id: it.product_id,
              batch_no: it.batch_no || '',
              quantity: it.quantity != null ? it.quantity : '',
              unit: it.unit || 'Kg',
              packaging_qty: it.packaging_qty != null ? it.packaging_qty : '',
              remarks: it.remarks || '',
            }))
          : [blankItem()],
      });
    }
  }, [gatePass]);

  // Load parties (customers for outward, vendors for inward) + products
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const partyFetcher = form.direction === 'outward'
          ? chemicalService.getCustomers
          : chemicalService.getVendors;

        const [partiesRes, productsRes] = await Promise.all([
          partyFetcher({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setParties((partiesRes.data || []).filter((p) => p.is_active));
        setProducts((productsRes.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error('Error loading data:', err);
        alert('❌ Failed to load data');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, [form.direction]);

  // When direction changes (and not editing), reset party_id since it must now be the other type
  useEffect(() => {
    if (!isEdit) {
      setForm((prev) => ({ ...prev, party_id: '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.direction]);

  // ─── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'product_id' && value) {
        const product = products.find((p) => p.id === parseInt(value, 10));
        if (product?.base_unit) items[index].unit = product.base_unit;
      }
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));
  };

  const removeItem = (index) => {
    if (form.items.length === 1) {
      alert('❌ A gate pass needs at least one item.');
      return;
    }
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.party_id) {
      const lbl = form.direction === 'outward' ? 'customer' : 'vendor';
      return alert(`❌ Select a ${lbl}.`);
    }
    if (!form.rider_name.trim()) return alert('❌ Rider name is required.');
    if (!form.vehicle_no.trim()) return alert('❌ Vehicle number is required.');
    if (!form.gp_date) return alert('❌ Gate pass date is required.');

    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      const idx = i + 1;
      if (!item.product_id) return alert(`❌ Item ${idx}: select a product.`);
      if (!item.quantity || parseFloat(item.quantity) <= 0) return alert(`❌ Item ${idx}: quantity must be > 0.`);
      if (!item.unit) return alert(`❌ Item ${idx}: unit is required.`);
    }

    try {
      setSubmitting(true);
      const payload = {
        direction: form.direction,
        gp_date: form.gp_date,
        party_type: form.direction === 'outward' ? 'customer' : 'vendor',
        party_id: parseInt(form.party_id, 10),
        rider_name: form.rider_name.trim(),
        rider_cnic: form.rider_cnic.trim() || null,
        rider_phone: form.rider_phone.trim() || null,
        vehicle_no: form.vehicle_no.trim(),
        status: form.status,
        remarks: form.remarks.trim() || null,
        items: form.items.map((item) => ({
          product_id: parseInt(item.product_id, 10),
          batch_no: item.batch_no.trim() || null,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          packaging_qty: item.packaging_qty === '' ? null : parseFloat(item.packaging_qty),
          remarks: item.remarks.trim() || null,
        })),
      };

      if (isEdit) {
        await chemicalService.updateGatePass(gatePass.id, payload);
        alert('✅ Gate pass updated successfully!');
      } else {
        await chemicalService.createGatePass(payload);
        alert('✅ Gate pass created! Auto-numbered by the system.');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save gate pass';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const partyLabel = form.direction === 'outward' ? 'Customer' : 'Vendor';

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Gate Pass' : '🚚 New Gate Pass'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit
                ? `Updating ${gatePass.gp_no}`
                : 'Record physical movement of goods'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Direction Switch ─────────── */}
            <div style={styles.directionSwitch}>
              <button
                type="button"
                onClick={() => handleChange('direction', 'outward')}
                style={{
                  ...styles.directionButton,
                  ...(form.direction === 'outward' ? styles.directionButtonActiveOutward : {}),
                }}
                disabled={isEdit}
              >
                ↑ Outward (to customer)
              </button>
              <button
                type="button"
                onClick={() => handleChange('direction', 'inward')}
                style={{
                  ...styles.directionButton,
                  ...(form.direction === 'inward' ? styles.directionButtonActiveInward : {}),
                }}
                disabled={isEdit}
              >
                ↓ Inward (from vendor)
              </button>
            </div>
            {isEdit && (
              <div style={styles.lockedHint}>🔒 Direction cannot be changed after creation</div>
            )}

            {/* ─── Header info ──────────────── */}
            <div style={styles.sectionLabel}>Header</div>
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  {partyLabel} <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.party_id}
                  onChange={(e) => handleChange('party_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingMaster}
                >
                  <option value="">
                    {loadingMaster
                      ? `Loading ${partyLabel.toLowerCase()}s...`
                      : parties.length === 0
                        ? `-- No active ${partyLabel.toLowerCase()}s --`
                        : `-- Select ${partyLabel.toLowerCase()} --`}
                  </option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.gp_date}
                  onChange={(e) => handleChange('gp_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  style={styles.input}
                >
                  <option value="issued">▶ Issued</option>
                  <option value="delivered">✓ Delivered</option>
                  <option value="returned">↩ Returned</option>
                </select>
              </div>
            </div>

            {/* ─── Rider & Vehicle ────────── */}
            <div style={styles.sectionLabel}>Rider & Vehicle</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Rider Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.rider_name}
                  onChange={(e) => handleChange('rider_name', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Imran Ali"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider CNIC</label>
                <input
                  type="text"
                  value={form.rider_cnic}
                  onChange={(e) => handleChange('rider_cnic', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 33100-1234567-8"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider Phone</label>
                <input
                  type="tel"
                  value={form.rider_phone}
                  onChange={(e) => handleChange('rider_phone', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. +92 300 1234567"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Vehicle No <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.vehicle_no}
                  onChange={(e) => handleChange('vehicle_no', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. FDD-3247"
                  required
                />
              </div>
            </div>

            {/* ─── Items ────────────────────── */}
            <div style={styles.sectionLabel}>Items ({form.items.length})</div>
            <div style={styles.itemsList}>
              {form.items.map((item, index) => (
                <div key={index} style={styles.itemCard}>
                  <div style={styles.itemHeader}>
                    <div style={styles.itemIndex}>{index + 1}</div>
                    <span style={styles.itemTitle}>Item {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={styles.removeButton}
                      disabled={form.items.length === 1}
                    >
                      🗑️
                    </button>
                  </div>

                  <div style={styles.itemRow}>
                    <div style={{ flex: 3, minWidth: '200px' }}>
                      <label style={styles.miniLabel}>Product *</label>
                      <select
                        value={item.product_id}
                        onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                        style={styles.input}
                        required
                        disabled={loadingMaster}
                      >
                        <option value="">-- Select --</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.code} — {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 2, minWidth: '140px' }}>
                      <label style={styles.miniLabel}>Batch No</label>
                      <input
                        type="text"
                        value={item.batch_no}
                        onChange={(e) => handleItemChange(index, 'batch_no', e.target.value)}
                        style={styles.input}
                        placeholder="optional"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '100px' }}>
                      <label style={styles.miniLabel}>Qty *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        style={styles.input}
                        placeholder="0"
                        required
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '90px' }}>
                      <label style={styles.miniLabel}>Unit *</label>
                      <select
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        style={styles.input}
                        required
                      >
                        {UNIT_OPTIONS.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '100px' }}>
                      <label style={styles.miniLabel}>Packaging</label>
                      <input
                        type="text"
                        value={item.packaging_qty}
                        onChange={(e) => handleItemChange(index, 'packaging_qty', e.target.value)}
                        style={styles.input}
                        placeholder="e.g. 2 bags"
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '6px' }}>
                    <label style={styles.miniLabel}>Item Remarks</label>
                    <input
                      type="text"
                      value={item.remarks}
                      onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                      style={styles.input}
                      placeholder="Optional notes about this item..."
                    />
                  </div>
                </div>
              ))}
            </div>

            <button type="button" onClick={addItem} style={styles.addItemButton}>
              + Add item
            </button>

            {/* ─── Remarks ─────────────────── */}
            <div style={styles.sectionLabel}>Remarks</div>
            <div style={styles.formGroup}>
              <textarea
                value={form.remarks}
                onChange={(e) => handleChange('remarks', e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Optional gate pass remarks..."
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Gate Pass' : '🚚 Issue Gate Pass')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '1000px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },

  directionSwitch: {
    display: 'flex',
    gap: '0',
    marginBottom: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    padding: '4px',
    background: '#f8f9fa',
  },
  directionButton: {
    flex: 1,
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    color: '#7f8c8d',
    transition: 'all 0.2s',
  },
  directionButtonActiveOutward: {
    background: '#e74c3c',
    color: 'white',
  },
  directionButtonActiveInward: {
    background: '#3498db',
    color: 'white',
  },
  lockedHint: {
    fontSize: '11px', color: '#856404',
    background: '#fffbea',
    padding: '6px 12px',
    borderRadius: '6px',
    marginBottom: '12px',
    fontWeight: '600',
  },

  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginTop: '20px', marginBottom: '12px',
    paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: {
    fontSize: '11px', fontWeight: '600',
    color: '#7f8c8d', marginBottom: '4px', display: 'block',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },

  itemsList: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    marginBottom: '12px',
  },
  itemCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '14px',
    border: '1px solid #e0e0e0',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px dashed #e0e0e0',
  },
  itemIndex: {
    width: '24px', height: '24px',
    borderRadius: '50%',
    background: '#e74c3c',
    color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700',
    flexShrink: 0,
  },
  itemTitle: {
    fontSize: '13px', fontWeight: '700', color: '#7f8c8d',
    textTransform: 'uppercase', letterSpacing: '0.3px', flex: 1,
  },
  itemRow: {
    display: 'flex', gap: '10px', flexWrap: 'wrap',
  },
  removeButton: {
    padding: '6px 12px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  addItemButton: {
    width: '100%',
    padding: '12px',
    background: 'white',
    color: '#e74c3c',
    border: '2px dashed #e74c3c',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px', fontWeight: '700',
    marginBottom: '12px',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default GatePassFormModal;